<script lang="ts">
import {
  defineComponent,
  ref,
  reactive,
  computed,
  onMounted,
  toRef,
} from 'vue'
import EventIntro from './_partials/EventIntro.vue'
import ButtonDefault from '@/components/button/ButtonDefault.vue'
import ButtonSubmit from '@/components/button/ButtonSubmit.vue'
import InputLead from '@/components/lead/InputLead.vue'
import UserLeadInfo from '@/components/lead/UserLeadInfo.vue'
import InputCustomFieldItems from '@/components/customFiled/InputCustomFieldItems.vue'
import MultiSelectorForm from '@/components/form/MultiSelectorForm.vue'
import MaterialIcon from '@/components/_core/MaterialIcon.vue'
import MaskContentForGuest from '@/components/cta/MaskContentForGuest.vue'
import MessageSection from '@/components/message/MessageSection.vue'
import PrivacyPolicyAgreement from '@/components/solutions/PrivacyPolicyAgreement.vue'
import EVENT_STATUS from '@/constants/eventStatus'
import EVENT_WAITLIST_STATUS from '@/constants/eventWaitlistStatus'
import stateInitializer from '@/models/stateInitializer'
import customFields from '@/models/customFields'
import leadGen from '@/models/leadGen'
import userCareer from '@/models/userCareer'
import { formatEventOfferLabel } from '@/models/eventOffer'
import privacyPolicy from '@/pages/_partials/solutionContents/privacyPolicy'
import { useUserStore } from '@/stores/user'
import dateUtil from '@/utilities/date'


export default defineComponent({
  name: 'EventMain',

  components: {
    EventIntro,
    InputLead,
    UserLeadInfo,
    ButtonDefault,
    ButtonSubmit,
    InputCustomFieldItems,
    MultiSelectorForm,
    MaterialIcon,
    MaskContentForGuest,
    MessageSection,
    PrivacyPolicyAgreement,
  },

  setup() {
    // store
    const userStore = useUserStore()

    interface State {
      event: SolutionEventItem,
      form: {
        register: Form,
        cancel: Form,
      },
      input: {
        userInfo: {
          lastName: InputText,
          firstName: InputText,
          lastNameKana: InputText,
          firstNameKana: InputText,
          companyName: InputText,
          industry: InputText,
          scale: InputText,
          listingStatus: InputText,
          profession: InputText,
          professionDomain: InputMultiSelect,
          positionLevel: InputText,
          departmentAndPosition: InputText,
          workMailAddress: InputText,
          phoneNumber: InputText,
          corporateZipcode: InputText,
          corporateAddress1: InputText,
          corporateAddress2: InputText,
          corporateNumber: InputText,
          corporateEmployeeNum: InputNumber,
        },
        customFields: Inputs,
        selected: {
          event: InputMultiSelect,
        },
        isFullFilled: boolean,
      },
      customFieldConfigItems?: CustomFieldConfig[],
      waitlist: {
        status: Maybe<string>,
      },
    }
    const state: State = reactive({
      event: {
        id: 0,
        title: '',
        status: '',
        thumbImageUrl: '',
        description: '',
        introduction: '',
        date: {
          from: '',
          to: '',
        },
        isFull: false,
        isValid: false,
        isTargetUser: false,
        solution: {
          id: 0,
          title: '',
          logoImageUrl: '',
          company: {
            name: '',
          },
          privacyPolicy: '',
        },
        items: [],
        entryRequirement: '',
      },
      form: {
        register: {
          name: '',
          action: '',
          method: 'post',
        },
        cancel: {
          name: '',
          action: '',
          method: 'delete',
        },
      },
      input: {
        selected: {
          event: {
            name: '',
            value: [],
            validationScheme: {
              required: true,
            },
          },
        },
        userInfo: {
          lastName: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          firstName: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          lastNameKana: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
              kana: true,
            },
          },
          firstNameKana: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
              kana: true,
            },
          },
          companyName: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          industry: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          scale: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          listingStatus: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          profession: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          professionDomain: {
            value: [],
            name: '',
            validationScheme: {},
          },
          positionLevel: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          departmentAndPosition: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
          workMailAddress: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
              email: true,
              mailDomain: true,
            },
          },
          phoneNumber: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
              phoneNumber: true,
            },
          },
          corporateZipcode: {
            value: '',
            name: '',
            validationScheme: {},
          },
          corporateAddress1: {
            value: '',
            name: '',
            validationScheme: {},
          },
          corporateAddress2: {
            value: '',
            name: '',
            validationScheme: {},
          },
          corporateNumber: {
            value: '',
            name: '',
            validationScheme: {},
          },
          corporateEmployeeNum: {
            value: undefined,
            name: '',
            validationScheme: {},
          },
        },
        customFields: {},
        isFullFilled: false,
      },
      customFieldConfigItems: [],
      waitlist: {
        status: null,
      },
    })

    stateInitializer(state, 'main')

    const { isShowProfessionDomain } = userCareer(toRef(state.input, 'userInfo'))
    const {
      // ref
      isEditModeUserLeadInfo,

      // computed
      isUpdateUserLeadInfo,

      // methods
      selectCompanyLeadInfo,
      inputCompanyName,
      toEditModeUserLeadInfo,
      toEditModeUserLeadInfoByError,
    } = leadGen(state.input.userInfo)

    // サーバ側の検証（valid?(:contact)）で不備があれば編集モードで開始する（isFullFilled は view_props 由来で setup 中に確定している）
    if (!state.input.isFullFilled) toEditModeUserLeadInfo()

    const { privacyPolicyCaption } = privacyPolicy(state.event.solution!)

    const initialized = ref(false)
    // 申込済みかどうかは view_props の選択済み枠（selected_event_offer_ids）で決まり、stateInitializer が setup 中に同期で埋めるため生成時に確定できる。
    // onMounted で後から true にすると、初回描画で申込フォーム（FormDefault, init-validation）が一瞬 mount → unmount され、
    // nextTick の validate() が unmount 後の ref を触って TypeError になる
    const isEventRegistered = ref(state.input.selected.event.value.length > 0)
    const isShowCancelModal = ref(false)

    customFields(state.input.customFields, state.customFieldConfigItems)

    /**
     * 受付終了日時を過ぎているか（現在が endsAt より後。厳密には終了日時ちょうどは含まない）。
     * endsAt は各枠の申込受付終了日時で、未設定枠はサーバーが前営業日16:00を算出するため必ず値を持つ。
     */
    const isEntryEnded = (endsAt: string) => dateUtil.isPast(new Date(endsAt))
    /**
     * 受付開始日時にまだ達していないか（現在が startsAt より前）。
     * startsAt は各枠の申込受付開始日時で、未設定枠は null のため呼び出し側で除外する。
     */
    const isBeforeEntryStart = (startsAt: string) => dateUtil.isBefore(new Date(), new Date(startsAt))

    /**
     * 開催枠が1件以上ありその全てが受付終了なら true（枠0件は false）。
     */
    const isEntryClosed = computed(() => {
      const items = state.event.items
      if (!items || items.length === 0) return false
      return items.every(item => !!item.entryPeriod?.to && isEntryEnded(item.entryPeriod.to))
    })
    /**
     * 受付期間が設定された枠（from あり）が1つ以上ありその全てが受付開始前なら true。
     * 全枠が未設定（デフォルト）の場合は受付開始の概念がないため false。
     */
    const isEntryNotStarted = computed(() => {
      const items = state.event.items
      if (!items) return false
      const startsAtList = items
        .map(item => item.entryPeriod?.from)
        .filter((from): from is string => !!from)
      return startsAtList.length > 0 && startsAtList.every(from => isBeforeEntryStart(from))
    })

    const eventDateList = computed(() => {
      if (!state.event.items) return []
      return state.event.items
        // イベントページの item は常に開催枠なので id は null にならないが、型上の null を除外しておく
        .filter((item): item is EventOfferItem & { id: number } => item.id !== null && !!item.date?.from && !!item.date?.to)
        .map(item => {
          const isAccepting = item.isAccepting !== false
          const from = item.entryPeriod?.from
          const isBeforeStart = !isAccepting && !!from && isBeforeEntryStart(from)
          const dateLabel = formatEventOfferLabel(item.date!.from, item.displayLabel)
          const suffix = isAccepting ? '' : (isBeforeStart ? '（受付開始前）' : '（受付終了）')
          return {
            value: item.id,
            label: `${dateLabel}${suffix}`,
            disabled: !isAccepting,
          }
        })
    })

    const eventStatus = computed(() => {
      if (state.event.status && state.event.status !== EVENT_STATUS.ACTIVE) {
        return EVENT_STATUS.PRIVATE
      }

      const endsAt = state.event.date?.to
        ? new Date(state.event.date.to)
        : undefined
      if (!!endsAt && dateUtil.isPast(endsAt)) {
        return EVENT_STATUS.ENDED
      }

      if (isEntryNotStarted.value) {
        return EVENT_STATUS.ENTRY_NOT_STARTED
      }

      if (isEntryClosed.value) {
        return EVENT_STATUS.ENTRY_CLOSED
      }

      // 受付待機（満席）の表示は、この閲覧者が申し込める（対象者である）ときだけ出す。
      // isFull は予算到達時など対象外ユーザーでも true になり得るが、対象外は待機にも申し込めないため
      // ラベル「受付待機（キャンセル待ち）で受付中」を出さず、申込不可メッセージ「対象者のみ」に委ねる
      if (state.event.isFull && state.event.isTargetUser) {
        return EVENT_STATUS.WAITLIST
      }

      return EVENT_STATUS.ACTIVE
    })

    /**
     * eventStatus が「申込可能」（ACTIVE または WAITLIST）かどうか。
     * フォーム表示・送信可否の判定で複数箇所参照するため計算プロパティに切り出す。
     */
    const isEntryOpen = computed(() => (
      eventStatus.value === EVENT_STATUS.ACTIVE || eventStatus.value === EVENT_STATUS.WAITLIST
    ))

    /**
     * 申込不可時のメッセージレベル識別カテゴリー
     * - 申込済み（確定）: notice
     * - 申込済み（未確定・受付待機中）: warning
     * - 未申込（対象外等）: alert
     */
    const eventStatusMessageCategory = computed(() => {
      if (!isEventRegistered.value) return 'alert'
      return state.waitlist.status === EVENT_WAITLIST_STATUS.WAITING
        ? 'warning'
        : 'notice'
    })

    const eventStatusLabel = computed(() => {
      // イベント終了・取り下げ・受付停止（予算の確定済み等）後は確定が来ないため、
      // 待機中の案内より終了系・受付停止の表示を優先する
      if (
        isEventRegistered.value
        && state.waitlist.status === EVENT_WAITLIST_STATUS.WAITING
        && state.event.isValid
        && eventStatus.value !== EVENT_STATUS.ENDED
        && eventStatus.value !== EVENT_STATUS.PRIVATE
      ) {
        return '現在、受付待機（キャンセル待ち）中です。ご参加いただけるようになりましたら、メールでご連絡いたします。'
      }

      switch (eventStatus.value) {
        case EVENT_STATUS.PRIVATE:
          return '本イベントは現在受付を停止しています'
        case EVENT_STATUS.ENDED:
          return '本イベントは終了しました'
        case EVENT_STATUS.ENTRY_NOT_STARTED:
          return '申し込み受付は開始されていません'
        case EVENT_STATUS.ENTRY_CLOSED:
          return '申し込み受付は終了しました'
      }

      return !state.event.isValid
        ? '本イベントは現在受付を停止しています'  // Solution の予算・単価が設定されてない等
        : !state.event.isTargetUser
          ? '本イベントは対象者のみお申し込みいただけます'
          : isEventRegistered.value
            ? '本イベントへはお申し込み済みです'
            : ''
    })

    onMounted(() => {
      // 「担当領域」を表示する場合は、「担当領域」のバリデーションを必須にする
      if (isShowProfessionDomain.value) {
        Object.assign(
          state.input.userInfo.professionDomain,
          { validationScheme: { required: true } },
        )
      }
    })

    const getEventOfferLabelById = (eventOfferId: number) => {
      const item = state.event.items?.find(item => item.id === eventOfferId)
      if (!item?.date?.from) return { from: undefined, label: '' }

      return {
        from: item.date.from,
        label: formatEventOfferLabel(item.date.from, item.displayLabel),
      }
    }

    /**
     * 「担当領域」の入力が必要な「職種」が選択された場合の処理
     */
    const onSelectedProfessionOptionLinkedToDomain = () => {
      Object.assign(
        state.input.userInfo.professionDomain,
        { validationScheme: { required: true } },
      )
    }

    /**
     * 「担当領域」の入力が不要な「職種」が選択された場合の処理
     */
    const onUnselectedProfessionOptionLinkedToDomain = () => {
      Object.assign(
        state.input.userInfo.professionDomain,
        { validationScheme: { required: false } },
      )
    }

    const onSelectInHearingMultiSelectorPrimary = () => {
      // no-op: hearing fields are not used on event page
    }

    const onCloseHearingMultiSelectorSecondary = () => {
      // no-op: hearing fields are not used on event page
    }

    const onValidated = () => {
      if (!initialized.value) initialized.value = true
    }

    return {
      // constants
      EVENT_STATUS,

      // store
      userStore,

      // data
      state,
      isEditModeUserLeadInfo,
      isEventRegistered,
      isShowCancelModal,
      initialized,

      // computed
      isUpdateUserLeadInfo,
      eventDateList,
      eventStatus,
      eventStatusLabel,
      eventStatusMessageCategory,
      isEntryOpen,
      privacyPolicyCaption,

      // methods
      getEventOfferLabelById,
      selectCompanyLeadInfo,
      inputCompanyName,
      toEditModeUserLeadInfo,
      toEditModeUserLeadInfoByError,
      onSelectedProfessionOptionLinkedToDomain,
      onUnselectedProfessionOptionLinkedToDomain,
      onSelectInHearingMultiSelectorPrimary,
      onCloseHearingMultiSelectorSecondary,
      onValidated,
    }
  },
})
</script>

<template>
  <section class="section">
    <div
      class="l-cols__event"
      :data-event-registered="isEventRegistered">
      <div class="l-cols__event--col is-summary">
        <EventIntro
          :item="state.event"
          :event-status="eventStatus">
          <template
            v-if="state.event.entryRequirement"
            #additional>
            <dl class="dl">
              <dt class="dt u-text__bold">
                申込可能な条件
              </dt>
              <dd class="dd u-text__preWrap">
                {{ state.event.entryRequirement }}
              </dd>
            </dl>
          </template>
        </EventIntro>
      </div>

      <div
        id="section-user-lead-info"
        class="l-cols__event--col is-userInfo">
        <section class="f-section__userInfo">
          <div class="f-section__userInfo--body">
            <!--
              以下の場合は申し込み不可のため、フォームを表示しない

              * イベントへ申し込み済み
              * イベントのステータスが「申込可能」以外
              * イベントの参加対象者でない
            -->
            <template v-if="isEventRegistered || !isEntryOpen || !state.event.isTargetUser">
              <MessageSection :status="eventStatusMessageCategory">
                <template #description>
                  <p class="p u-text__small u-text__center">
                    {{ eventStatusLabel }}
                  </p>
                </template>
              </MessageSection>

              <!-- イベント申込済みの場合のみ、参加予定日を表示 -->
              <template v-if="isEventRegistered">
                <div class="o-list__registeredEventOffer">
                  <dl class="o-list__registeredEventOffer--unit">
                    <dt class="o-list__registeredEventOffer--term">
                      <span class="o-list__registeredEventOffer--icon">
                        <MaterialIcon name="CalendarMonthOutline" />
                      </span>
                      参加予定日
                    </dt>
                    <dd
                      v-for="item in state.input.selected.event.value"
                      :key="item"
                      class="o-list__registeredEventOffer--description">
                      <time
                        v-if="item"
                        class="time u-text__block"
                        :datetime="getEventOfferLabelById(Number(item)).from">
                        {{ getEventOfferLabelById(Number(item)).label }}
                      </time>
                    </dd>
                  </dl>
                <!-- /.o-list__registeredEventOffer --></div>

                <!--
                キャンセル機能は一旦見送りのためコメントアウト
                <template v-if="eventStatus !== EVENT_STATUS.ENDED">
                  <p class="p u-text__center u-margin__larger">
                    <button
                      type="button"
                      class="button u-text__small"
                      @click="isShowCancelModal = true">
                      キャンセル
                    </button>
                  </p>

                  <div class="o-note__general u-text__small">
                    <p class="o-note__general--item">イベントの参加日程を変更する場合は、一度キャンセルのうえ、再度お申し込みください。</p>
                  </div>
                </template>
                -->
              </template>
            </template>

            <template v-else>
              <!-- ログイン時 -->
              <FormDefault
                v-if="userStore.context.isSignedIn"
                :name="state.form.register.name"
                :action="state.form.register.action"
                :method="state.form.register.method"
                :init-validation="true"
                @validated="onValidated"
                @error="toEditModeUserLeadInfoByError">
                <MessageSection
                  v-if="eventStatus === EVENT_STATUS.WAITLIST"
                  status="warning">
                  <template #header>
                    <h2 class="h2 u-text__small u-text__bold u-text__center">
                      現在、お申込み上限に達しております。
                    </h2>
                  </template>
                  <template #description>
                    <p class="p u-text__x-small">
                      予約応募（確約なし）での受付となりますので<span class="span u-text__block">ご了承ください。</span>
                    </p>

                    <div class="o-note__general u-text__x-small">
                      <p class="o-note__general--item">
                        申込上限の増加/キャンセルなどが生じた際に、<span class="span u-text__block">ご参加可能となります。</span>
                      </p>
                    <!-- /.o-note__general --></div>
                  </template>
                </MessageSection>

                <div class="l-unit__userLeadInfo">
                  <div class="l-unit__userLeadInfo--description">
                    <!-- リード情報入力エリア -->
                    <InputLead
                      v-if="isEditModeUserLeadInfo"
                      :namespace="state.form.register.name"
                      :user-lead-info="state.input.userInfo"
                      @update:last-name="state.input.userInfo.lastName.value = $event"
                      @update:first-name="state.input.userInfo.firstName.value = $event"
                      @update:last-name-kana="state.input.userInfo.lastNameKana.value = $event"
                      @update:first-name-kana="state.input.userInfo.firstNameKana.value = $event"
                      @update:company-name="inputCompanyName($event)"
                      @update:industry="state.input.userInfo.industry.value = $event"
                      @update:scale="state.input.userInfo.scale.value = $event"
                      @update:listing-status="state.input.userInfo.listingStatus.value = $event"
                      @update:profession="state.input.userInfo.profession.value = $event"
                      @update:profession-domain="state.input.userInfo.professionDomain.value = $event"
                      @open:profession-domain="onSelectedProfessionOptionLinkedToDomain"
                      @close:profession-domain="onUnselectedProfessionOptionLinkedToDomain"
                      @update:position-level="state.input.userInfo.positionLevel.value = $event"
                      @update:department-and-position="state.input.userInfo.departmentAndPosition.value = $event"
                      @update:work-mail-address="state.input.userInfo.workMailAddress.value = $event"
                      @update:phone-number="state.input.userInfo.phoneNumber.value = $event"
                      @select:company-lead-info="selectCompanyLeadInfo($event)"
                      @reset:corporate-zipcode="state.input.userInfo.corporateZipcode.value = $event">
                      <template #heading>
                        イベントに参加するには<span class="span u-text__block">
                          次の項目をご入力ください
                        </span>
                      </template>
                    </InputLead>

                    <UserLeadInfo
                      v-else
                      :user-lead-info="state.input.userInfo"
                      :namespace="state.form.register.name"
                      @reset:corporate-zipcode="state.input.userInfo.corporateZipcode.value = $event" />

                    <InputCustomFieldItems
                      v-if="state.input.customFields && state.customFieldConfigItems && initialized"
                      :namespace="state.form.register.name"
                      :custom-field-config-items="state.customFieldConfigItems"
                      :input-custom-fields="state.input.customFields"
                      @update:custom-fields="Object.assign(state.input.customFields, $event)" />

                    <MultiSelectorForm
                      v-if="state.input.selected.event.name && initialized && isEntryOpen"
                      v-model="state.input.selected.event.value"
                      header="参加日"
                      :namespace="state.form.register.name"
                      :name="state.input.selected.event.name"
                      :items="eventDateList"
                      :validation-scheme="state.input.selected.event.validationScheme" />
                  <!-- /.l-unit__userLeadInfo description --></div>

                  <div class="l-unit__userLeadInfo--action is-submit">
                    <PrivacyPolicyAgreement :company-name="state.event.solution?.company?.name" />

                    <ButtonSubmit
                      name="commit"
                      :is-link="false"
                      :caption="privacyPolicyCaption"
                      :disabled="!isEntryOpen || !state.event.isTargetUser"
                      is-submit>
                      {{ eventStatus === EVENT_STATUS.WAITLIST ? '参加予約に応募する' : 'イベントへ申し込む' }}
                    </ButtonSubmit>

                    <div
                      v-if="eventStatus === EVENT_STATUS.WAITLIST"
                      class="o-note__general u-text__x-small">
                      <p class="o-note__general--item">
                        確約ではありません。<span class="span u-text__block">ご参加いただけるようになりましたら、メールでご連絡します。</span>
                      </p>
                    <!-- /.o-note__general --></div>
                  <!-- /.l-unit__userLeadInfo action --></div>

                  <div
                    v-if="!isEditModeUserLeadInfo"
                    class="l-unit__userLeadInfo--action">
                    <p class="p u-text__center u-text__small">
                      <button
                        type="button"
                        class="button"
                        @click="toEditModeUserLeadInfo">
                        プロフィールを編集する
                      </button>
                    </p>
                  <!-- /.l-unit__userLeadInfo action --></div>
                <!-- /.l-unit__userLeadInfo --></div>

                <input
                  class="input"
                  type="hidden"
                  name="contact[message]"
                  value="">

                <input
                  v-if="isUpdateUserLeadInfo"
                  class="input"
                  type="hidden"
                  name="contact[with_update]"
                  value="1">
              </FormDefault>

              <section
                v-else
                class="section">
                <MaskContentForGuest
                  :has-fade="false"
                  :has-heading-secondary="false">
                  <template #headingPrimary>
                    イベントへのお申し込みにはログインが必要です
                  </template>
                </MaskContentForGuest>
              </section>
            </template>
          </div>
        </section>
      </div>
    </div>

    <ModalDefault
      v-model:is-show="isShowCancelModal"
      size="large"
      is-blur>
      <template #header>
        イベントの参加をキャンセルしますがよろしいですか？
      </template>

      <template #description>
        <FormDefault
          :name="state.form.cancel.name"
          :action="state.form.cancel.action"
          :method="state.form.cancel.method">
          <div
            class="l-cols is-col2"
            data-breakpoint="sp">
            <div class="l-cols--col">
              <ButtonDefault
                emit-name="click:close"
                :is-link="false"
                is-font-small
                is-large
                prevent
                @click:close="isShowCancelModal = false">
                閉じる
              </ButtonDefault>
            <!-- /.l-cols col --></div>

            <div class="l-cols--col">
              <ButtonSubmit
                :is-link="false"
                is-font-small
                is-large
                is-submit>
                次へ
              </ButtonSubmit>
            <!-- /.l-cols col --></div>
          <!-- /.l-cols --></div>
        </FormDefault>
      </template>
    </ModalDefault>
  </section>
</template>

<style lang="sass" scoped>
// foundation
.section
.f-section__userInfo
  $root: &

  & &--body
    padding: 24px
    background-color: rgba($bg-white, 1)

  +breakpoint
    #{$root}--body
      padding: 16px

// layout
.l-cols__event
  $root: &
  width: 100%
  display: flex
  align-items: flex-start
  position: relative
  z-index: 1

  $margin-size: 40
  $margin-percent: getRelativeValue($margin-size, $global-contents-width-pc)
  $col-width: math.div(($global-contents-width-pc - $margin-size), 2)
  & &--col
    width: getRelativeValue($col-width, $global-contents-width-pc)
    margin-left: $margin-percent

    &:first-child
      margin-left: 0

    &.is-userInfo
      position: sticky
      top: #{$global-section-prop-pc}px

  +breakpoint
    $margin-size: 20
    display: block

    #{$root}--col
      width: 100%
      margin:
        top: #{$margin-size}px
        left: 0

      &:first-child
        margin-top: 0

.l-unit__userLeadInfo
  & &--action
    &.is-submit
      padding: 16px 0

      &.is-sticky
        &[data-media-size="sp"],
        &[data-media-size="phablet"]
          width: calc(100% - #{$global-prop-sp * 2}px)
          position: fixed
          left: 50%
          bottom: 0
          transform: translateX(-50%)
          padding: 16px
          background: rgba($bg-white, 0.8)
          z-index: 1

// object
.o-list__registeredEventOffer
  $root: &
  margin-top: 1.5em

  &:first-child
    margin-top: 0

  & &--term
    display: flex
    align-items: center
    color: $font-light-gray
    font:
      size: $font-small
      weight: bold

  & &--icon
    width: 1em
    margin-right: 0.3em

  & &--description
    font:
      size: $font-small
      weight: bold
    margin-top: 0.5em
    padding: 1em
    background-color: rgba($bg-x-light-gray, 1)

    &:first-child
      margin-top: 0
</style>
